import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Filter, { FilterModel } from '../../components/Filter/Filter';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import { InvoiceStatusModel } from '../invoice-statuses/InvoiceStatusesIndex';
import { upsertFilterToKey } from '../../services/filterService';
import { sleep } from '../../services/utils';
import { WorkerModel } from '../../components/Invoice/InvoiceWorkers';
import CustomSelect from '../../components/CustomSelect/CustomSelect';

export class InvoiceFilterFields extends FilterModel {
  customer?: number | null;
  company?: number | null;
  transactionNature?: number | null;
  worker?: number | null;
  status: number[];
  isPaid?: boolean | null;
  isNotPaid?: boolean | null;
  withEstimate?: boolean | null;
  withoutEstimate?: boolean | null;

  constructor(data: any = {}) {
    super(data);
    this.customer = data.customer;
    this.company = data.company;
    this.transactionNature = data.transactionNature;
    this.worker = data.worker;
    this.status = data.status || [];
    this.isPaid = data.isPaid;
    this.isNotPaid = data.isNotPaid;
    this.withEstimate = data.withEstimate;
    this.withoutEstimate = data.withoutEstimate;
  }

  public get totalFilters(): number {
    let total = 0;
    if (this.customer) total += 1;
    if (this.company) total += 1;
    if (this.worker) total += 1;
    if (this.status && this.status.length) total += 1;
    if (this.isPaid) total += 1;
    if (this.isNotPaid) total += 1;
    if (this.q) total += 1;
    return total;
  }
}

interface InvoicesFilterProps {
  onSubmit: (data: InvoiceFilterFields) => void;
  filterFields: InvoiceFilterFields;
  pageKey: string;
  withCustomerFilter?: boolean;
}

export default function InvoicesFilter({
  onSubmit,
  filterFields,
  pageKey,
  withCustomerFilter = true,
}: InvoicesFilterProps) {
  const { control, register, handleSubmit, reset, setValue } =
    useForm<InvoiceFilterFields>({
      mode: 'onBlur',
    });
  const {
    isAdmin,
    user: { organization },
  } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  const [invoiceStatuses, setInvoiceStatuses] = useState(
    [] as InvoiceStatusModel[]
  );
  const [invoiceStatusesLoading, setinvoiceStatusesLoading] = useState(false);

  const [workers, setWorkers] = useState<WorkerModel[]>([]);
  const [workersLoading, setWorkersLoading] = useState(false);

  async function getCustomers() {
    if (organization) {
      setCustomersLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/customers`
      );
      setCustomers(response.data.data);
      await sleep(1000);
      setCustomersLoading(false);
    }
  }

  async function getInvoiceStatuses() {
    if (organization) {
      setinvoiceStatusesLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/invoice-statuses`
      );
      setInvoiceStatuses(response.data.data);
      await sleep(1000);
      setinvoiceStatusesLoading(false);
    }
  }

  async function getWorkers() {
    if (organization && isAdmin) {
      setWorkersLoading(true);
      const { data } = await api.get(
        `admin/organizations/${organization.id}/users/workers`
      );
      setWorkers(data.data);
      setWorkersLoading(false);
    }
  }

  const handleIsNotPaid = () => {
    setValue('isPaid', false);
    upsertFilterToKey(pageKey, filterFields);
  };

  const handleIsPaid = () => {
    setValue('isNotPaid', false);
    upsertFilterToKey(pageKey, filterFields);
  };

  const clear = () => {
    reset({
      customer: null,
      worker: null,
      isPaid: null,
      isNotPaid: null,
      q: null,
      status: [],
      skip: 0,
      take: 25,
    });
    upsertFilterToKey(pageKey, {});
  };

  useEffect(() => {
    reset(filterFields);
  }, [filterFields]);

  useEffect(() => {
    if (withCustomerFilter) {
      getCustomers();
    }
    getWorkers();
    getInvoiceStatuses();
  }, [organization, isAdmin]);

  return (
    <Filter
      isLoading={customersLoading && invoiceStatusesLoading && workersLoading}
      onSubmit={handleSubmit(onSubmit)}
      clear={clear}
      totalApplied={filterFields?.totalFilters || 0}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb-3'>
          <input
            {...register('q')}
            className='form-control'
            placeholder='Digite para pesquisar'
            type='text'
          />
        </div>
        <div className='mb-3'>
          <div className='form-check form-switch'>
            <input
              {...register('isPaid')}
              className='form-check-input'
              type='checkbox'
              role='switch'
              onChange={handleIsPaid}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-check-label'>
              Trazer apenas as pagas
            </label>
          </div>
        </div>
        <div className='mb-3'>
          <div className='form-check form-switch'>
            <input
              {...register('isNotPaid')}
              className='form-check-input'
              type='checkbox'
              role='switch'
              onChange={handleIsNotPaid}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor='inputLabel' className='form-check-label'>
              Trazer apenas as pendentes de pagamento
            </label>
          </div>
        </div>
        {withCustomerFilter && (
          <div className='mb-3'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='form-label'>Cliente</label>
            <Controller
              name='customer'
              control={control}
              render={({ field }) => (
                <CustomSelect
                  options={customers.map((customer: any) => ({
                    label: customer.name,
                    value: customer.id,
                  }))}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder='Selecione o Cliente'
                />
              )}
            />
          </div>
        )}
        {isAdmin && (
          <div className='mb-3'>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='form-label'>Colaborador</label>
            <Controller
              name='worker'
              control={control}
              render={({ field }) => (
                <CustomSelect
                  options={workers.map((worker: any) => ({
                    label: worker.name,
                    value: worker.id,
                  }))}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder='Selecione o Colaborador'
                />
              )}
            />
          </div>
        )}
        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            Status
          </label>
          {invoiceStatuses.map((c: InvoiceStatusModel) => (
            <div key={c.id} className='form-check'>
              <input
                className='form-check-input'
                type='checkbox'
                value={c.id}
                {...register('status')}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className='form-check-label' htmlFor='flexCheckDefault'>
                {c.label}
              </label>
            </div>
          ))}
        </div>
      </form>
    </Filter>
  );
}
